import { queryOptions, skipToken, useQuery } from '@tanstack/react-query'

import type { AdvisorCardDto } from '@brickup/shared/dto'
import type {
  ConfiguratorDisplayedProperty,
  ConfiguratorSelectionsDto,
  ConfiguratorTreeRes,
} from '@brickup/shared/dto/configurator'
import type {
  ConfiguratorGroupKind,
  ConfiguratorScope as ConfiguratorScopeEnum,
  FloorType,
  PropertyType,
  RoomType,
} from '@brickup/shared/enums'
import { COMMON } from '@brickup/shared/locales/de/common'
import type {
  ConfiguratorQuestion,
  CustomerCostEntry,
  CustomerProjectCard,
} from '@brickup/shared/models'
import { applySortOrder, mapDeep } from '@brickup/shared/utils'
import {
  CATALOGUE_TREE_KEY,
  CONFIGURATOR_CMS_QUESTIONS_KEY,
  CONFIGURATOR_TREE_KEY,
  CUSTOMER_POSITIONS_KEY,
  CUSTOMER_PROJECT_ADVISOR_KEY,
  CUSTOMER_PROJECT_KEY,
  CUSTOMER_PROJECTS_KEY,
  CUSTOMER_SELECTIONS_KEY,
  PLANNING_SELECTIONS_PREFIX,
  PUBLIC_CONFIGURATOR_TREE_KEY,
  PUBLIC_HOUSE_ELEMENT_TYPES_KEY,
} from './query-keys'
import { apiClient, publicClient } from '@/api/client'
import { makeCrudHooks, makeSortHook, type WriteAnswer } from '@/api/crud-hooks'
import type { QueryKeyList } from '@/api/query-keys'
import {
  useCustomerScope,
  useOptionalCustomerProjectId,
} from '@/contexts/customer-project-context'
import { useShadowUserId } from '@/contexts/shadow-context'
import { useMutationWithInvalidation } from '@/hooks/use-mutation-with-invalidation'
import {
  customerProjectBase,
  customerProjectsBase,
  projectScopedKey,
  shadowScopedKey,
  type CustomerScope,
  type MaybeProjectScope,
} from '@/utils/customer-shadow'

/** Which of the two trees a CMS screen edits — the Prisma enum, so a member
 *  added there is a build error here rather than a silent mismatch. */
export type ConfiguratorScope = ConfiguratorScopeEnum

// ────────── Configurator Tree ──────────

/**
 * Which admin tree to read. `scope` and `propertyType` name one tree exactly,
 * as the CMS edits it; each omitted widens to both. `projectId` asks for the
 * tree that project's property is configured against, which is the house tree
 * for an apartment while the apartment tree holds no question.
 */
export type AdminTreeQuery = {
  scope?: ConfiguratorScope
  propertyType?: PropertyType
  projectId?: number
}

// Admin tree (full data with pricing — requires ROOT, used by CMS)
async function getAdminConfiguratorTree(
  query: AdminTreeQuery,
): Promise<ConfiguratorTreeRes> {
  const { data } = await apiClient.get<ConfiguratorTreeRes>(
    '/admin/configurator/tree',
    { params: query },
  )
  return data
}

// Used by CMS tree view (admin) — full data, scope-aware
export function useConfiguratorTree(query: AdminTreeQuery = {}) {
  return useQuery({
    queryKey: [...CONFIGURATOR_TREE_KEY, 'admin', query],
    queryFn: () => getAdminConfiguratorTree(query),
    staleTime: 5 * 60 * 1000,
  })
}

// Public tree — unauthenticated, scope=PUBLIC, used by /public/configurator
async function getPublicConfiguratorTree(
  propertyType: PropertyType,
): Promise<ConfiguratorTreeRes> {
  const { data } = await publicClient.get<ConfiguratorTreeRes>(
    '/public/configurator/tree',
    { params: { propertyType } },
  )
  return data
}

/** The public tree for the kind of property being configured. */
export function usePublicConfiguratorTree(propertyType: PropertyType) {
  return useQuery({
    queryKey: [...PUBLIC_CONFIGURATOR_TREE_KEY, propertyType],
    queryFn: () => getPublicConfiguratorTree(propertyType),
    staleTime: 5 * 60 * 1000,
  })
}

// Slim HouseElementType catalog for the public configurator — seeds formula
// scope so admin formulas referencing a HouseElement key don't silently
// evaluate to null for unauthenticated visitors.
export type PublicHouseElementTypeSlim = {
  key: string
  mode: 'QUANTITY' | 'OPTION'
  scope: 'GLOBAL' | 'PER_ROOM'
  defaultOptionKey: string | null
}

async function getPublicHouseElementTypes(): Promise<
  PublicHouseElementTypeSlim[]
> {
  const { data } = await publicClient.get<PublicHouseElementTypeSlim[]>(
    '/public/configurator/house-element-types',
  )
  return data
}

export function usePublicHouseElementTypes() {
  return useQuery({
    queryKey: PUBLIC_HOUSE_ELEMENT_TYPES_KEY,
    queryFn: getPublicHouseElementTypes,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Everything the customer area reads and writes about one project.
 *
 * Each of these takes the `CustomerScope` — which customer, which project —
 * rather than threading a shadow id beside a project id. The hooks read it
 * from context, so a screen under a project route cannot address the wrong
 * one; the plain functions take it explicitly, for a route loader with no
 * React context around it.
 */

// Customer tree (with pricing — requires CUSTOMER auth, or advisor via shadow)
async function getCustomerConfiguratorTree(
  scope: CustomerScope,
): Promise<ConfiguratorTreeRes> {
  const { data } = await apiClient.get<ConfiguratorTreeRes>(
    `${customerProjectBase(scope)}/configurator/tree`,
  )
  return data
}

export function useCustomerConfiguratorTree() {
  const scope = useCustomerScope()
  return useQuery({
    queryKey: projectScopedKey([...CONFIGURATOR_TREE_KEY, 'customer'], scope),
    queryFn: () => getCustomerConfiguratorTree(scope),
    staleTime: 5 * 60 * 1000,
  })
}

// ────────── Customer: Projects + Selections ──────────

/** Every project this customer has, as a dashboard lists them and a picker
 *  offers them. The one read that names no project. */
export async function listCustomerProjects(
  shadowUserId?: number,
): Promise<CustomerProjectCard[]> {
  const { data } = await apiClient.get<CustomerProjectCard[]>(
    customerProjectsBase(shadowUserId),
  )
  return data
}

/** Shared with the route guards, which read the same entry before a screen
 *  mounts to decide which project to open. */
export const customerProjectsQuery = (shadowUserId?: number) =>
  queryOptions({
    queryKey: shadowScopedKey(CUSTOMER_PROJECTS_KEY, shadowUserId),
    queryFn: () => listCustomerProjects(shadowUserId),
    staleTime: 5 * 60 * 1000,
  })

export function useCustomerProjects() {
  return useQuery(customerProjectsQuery(useShadowUserId()))
}

export async function getCustomerProject(scope: CustomerScope) {
  try {
    const { data } = await apiClient.get(customerProjectBase(scope))
    return data
  } catch (error: any) {
    if (error?.response?.status === 404) return null
    throw error
  }
}

/**
 * Shared so a caller above the project provider — the shadow shell, which
 * spans screens with no project — reads the same cache entry.
 *
 * A scope naming no project asks for nothing: `skipToken` keeps the query
 * idle without a fabricated id reaching the key or the path.
 */
export const customerProjectQuery = ({
  shadowUserId,
  projectId,
}: MaybeProjectScope) =>
  queryOptions({
    queryKey: projectScopedKey(CUSTOMER_PROJECT_KEY, {
      shadowUserId,
      projectId,
    }),
    queryFn:
      projectId == null
        ? skipToken
        : () => getCustomerProject({ shadowUserId, projectId }),
    staleTime: 5 * 60 * 1000,
  })

export function useCustomerProject() {
  return useQuery(customerProjectQuery(useCustomerScope()))
}

async function getCustomerProjectAdvisor(
  scope: CustomerScope,
): Promise<AdvisorCardDto | null> {
  const { data } = await apiClient.get<AdvisorCardDto | null>(
    `${customerProjectBase(scope)}/advisor`,
  )
  return data ?? null
}

/**
 * The contact person assigned to this project — the first tier of
 * `useCustomerAdvisor`'s chain, and `null` when the project names nobody.
 *
 * Its own query: resolving it reaches HubSpot for the phone number and
 * booking link, which must not delay the project response.
 */
export function useCustomerProjectAdvisor(projectId?: number) {
  const shadowUserId = useShadowUserId()
  // The open project on a project screen; the one a summary screen names, on
  // the dashboard. Neither: there is no project tier to ask, and the chain
  // below starts at the customer's mandate instead.
  //
  // Read before the `??`, not inside it: a hook behind a short circuit is
  // skipped whenever a project is named, and a caller whose id arrives a
  // render late then changes the hook count between renders.
  const openProjectId = useOptionalCustomerProjectId()
  const id = projectId ?? openProjectId

  const query = useQuery(
    queryOptions({
      queryKey: projectScopedKey(CUSTOMER_PROJECT_ADVISOR_KEY, {
        shadowUserId,
        projectId: id,
      }),
      queryFn:
        id == null
          ? skipToken
          : () => getCustomerProjectAdvisor({ shadowUserId, projectId: id }),
      staleTime: 5 * 60 * 1000,
    }),
  )

  return {
    advisor: query.data ?? undefined,
    /**
     * Whether this tier has had its say. A screen with no project never asks,
     * and that counts as answered — the chain below it must move on to the
     * mandate rather than wait for a reply that is not coming.
     */
    isSettled: id == null || query.isFetched,
  }
}

export async function addCustomerPropertyPicture({
  file,
  scope,
}: {
  file: File
  scope: CustomerScope
}) {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await apiClient.post(
    `${customerProjectBase(scope)}/property/pictures`,
    formData,
  )
  return data
}

async function getCustomerSelections(
  scope: CustomerScope,
): Promise<ConfiguratorSelectionsDto> {
  const { data } = await apiClient.get<ConfiguratorSelectionsDto>(
    `${customerProjectBase(scope)}/configurator/selections`,
  )
  return data
}

/** `enabled: false` for a caller that cannot know the configurator is
 *  reachable — the endpoint 404s for INTERNAL status. */
export function useCustomerSelections(options?: { enabled?: boolean }) {
  const scope = useCustomerScope()
  return useQuery({
    queryKey: projectScopedKey(CUSTOMER_SELECTIONS_KEY, scope),
    queryFn: () => getCustomerSelections(scope),
    staleTime: 5 * 60 * 1000,
    enabled: options?.enabled ?? true,
  })
}

/** What the customer's configurator reads about one project's picks. */
export type CustomerPicks = {
  /** The positions an operator released — the part of the base renovation the
   *  Auswertung itemises by name. */
  positions: CustomerCostEntry[]
  /** Which picks the screen must refuse, by placement. Decided over every pick
   *  position, released or not: a hold is not a question about what the
   *  customer may see. */
  locks: ReadonlyMap<number, boolean>
}

async function getCustomerPicks(scope: CustomerScope): Promise<CustomerPicks> {
  const { data } = await apiClient.get<{
    positions: CustomerCostEntry[]
    locks: Record<string, boolean>
  }>(`${customerProjectBase(scope)}/configurator/positions`)

  return {
    positions: data.positions,
    // Keyed by placement the way every reader of it is, rather than by the
    // string a JSON object can hold.
    locks: new Map(
      Object.entries(data.locks).map(([placementId, locked]) => [
        Number(placementId),
        locked,
      ]),
    ),
  }
}

export function useCustomerPicks() {
  const scope = useCustomerScope()
  return useQuery({
    queryKey: projectScopedKey(CUSTOMER_POSITIONS_KEY, scope),
    queryFn: () => getCustomerPicks(scope),
    staleTime: 5 * 60 * 1000,
  })
}

async function saveCustomerSelections(
  dto: ConfiguratorSelectionsDto,
  scope: CustomerScope,
): Promise<ConfiguratorSelectionsDto> {
  const { data } = await apiClient.patch<ConfiguratorSelectionsDto>(
    `${customerProjectBase(scope)}/configurator/selections`,
    dto,
  )
  return data
}

/**
 * The Internal Planung table reads the same picks back through
 * `PLANNING_SELECTIONS_PREFIX`, so a save made in the shadow view has to bust
 * it too — and the save is what writes the positions, which is where every
 * quoted price and the "a fixed price will be lost" warning are read from.
 */
export function useSaveCustomerSelections() {
  const scope = useCustomerScope()
  return useMutationWithInvalidation(
    (dto: ConfiguratorSelectionsDto) => saveCustomerSelections(dto, scope),
    [
      projectScopedKey(CUSTOMER_SELECTIONS_KEY, scope),
      projectScopedKey(CUSTOMER_POSITIONS_KEY, scope),
      PLANNING_SELECTIONS_PREFIX,
    ],
  )
}

// ────────── CMS: CRUD + Sort Factories ──────────

// Every CMS write reshapes the tree, so the wrapper busts it and a caller
// passes only the keys its own entity adds.
const cmsCrudHooks = <TCreate, TCreated = unknown>(
  basePath: string,
  extraKeys: QueryKeyList = [],
) =>
  makeCrudHooks<TCreate, any, TCreated>(basePath, [
    CONFIGURATOR_TREE_KEY,
    ...extraKeys,
  ])

/** A question is held by the tree that renders it and by the catalogue the
 *  CMS lists it in, so a write that moves one between them busts both. */
const QUESTION_KEYS = [CONFIGURATOR_TREE_KEY, CONFIGURATOR_CMS_QUESTIONS_KEY]

/**
 * The answer a refusing write takes, as the request carries it.
 *
 * The same `answer` the factory's `usePatch` takes, so a refusal that needs a
 * second field — the reword already does — is taught one convention rather
 * than each hand-written hook. Axios drops an undefined key, so the first
 * attempt asks and the second answers, which is what makes a confirmation
 * given against a list that has moved on fail rather than apply.
 */
const answering = (answer?: WriteAnswer) => ({ params: answer })

// ────────── CMS: Groups ──────────

const groupsHooks = cmsCrudHooks<{
  /** Omitted on a storey or room group: it is named after what it stands
   *  for. */
  name?: string | null
  /** Null creates a root. A child takes its parent's scope and property
   *  type. */
  parentId?: number | null
  scope?: ConfiguratorScope
  propertyType?: PropertyType
  /** What kind of group it is. Chosen when it is added; a patch never
   *  changes it. */
  kind?: ConfiguratorGroupKind
  /** Which rooms and storeys it covers, within its parent coverage. Empty =
   *  all of them. */
  roomTypes?: RoomType[]
  floorTypes?: FloorType[]
}>('/admin/configurator/groups')
export const useCreateGroup = groupsHooks.useCreate
/**
 * Rename, move or renarrow a group.
 *
 * Every one of those can take a place away from a position somebody was
 * already quoted at — a rung narrowed to fewer rooms reaches fewer places, and
 * the server refuses the whole patch until that is confirmed. So the hook is
 * the factory's, which carries the answer: `answer` goes through
 * `useOrphanConfirm`, and a patch with nothing to answer sends nothing.
 */
export const usePatchGroup = groupsHooks.usePatch

/** Takes the node and everything under it out of the tree, so every question
 *  it held stops being asked — see `usePatchGroup` on the confirmation. */
export function useDeleteGroup() {
  return useMutationWithInvalidation(
    async ({ id, answer }: { id: number; answer?: WriteAnswer }) => {
      await apiClient.delete(
        `/admin/configurator/groups/${id}`,
        answering(answer),
      )
    },
    QUESTION_KEYS,
    { errorFallback: COMMON.errors.deleteFailed },
  )
}
export const useSortGroups = makeSortHook(
  '/admin/configurator/groups/sort',
  [CONFIGURATOR_TREE_KEY],
  // A group is `groups` at the root of the tree and `children` below it.
  { listKey: ['groups', 'children'] },
)

// ────────── CMS: Questions ──────────

/** What a placement row draws, from either source. */
export type QuestionPlacement = {
  id: number
  measureId: number
  title: string
  standardName: string | null
  isDefault: boolean
}

export type CmsQuestion = Pick<
  ConfiguratorQuestion,
  'id' | 'name' | 'selectionType'
> & {
  displayedProperties: ConfiguratorDisplayedProperty[]
  options: QuestionPlacement[]
  _count?: { measures?: number }
}

/**
 * Every question of one tree, placed or not, with what holds it.
 *
 * The catalogue is this endpoint rather than a walk of the rendered tree: a
 * question no group holds appears in no tree and would otherwise have no row.
 */
export function useConfiguratorCmsQuestions(scope?: ConfiguratorScope) {
  return useQuery({
    queryKey: scope
      ? [...CONFIGURATOR_CMS_QUESTIONS_KEY, scope]
      : CONFIGURATOR_CMS_QUESTIONS_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get('/admin/configurator/questions', {
        params: scope ? { scope } : undefined,
      })
      return data as CmsQuestion[]
    },
  })
}

const questionsHooks = cmsCrudHooks<
  {
    name: string
    selectionType: string
    scope?: ConfiguratorScope
  },
  // The created question comes back so the caller can place it in the group
  // it was added from, which is the only reason the CMS creates one.
  { id: number }
>('/admin/configurator/questions', [CONFIGURATOR_CMS_QUESTIONS_KEY])
export const useCreateQuestion = questionsHooks.useCreate
export const usePatchQuestion = questionsHooks.usePatch
export const useDeleteQuestion = questionsHooks.useDelete

// ────────── CMS: Question ↔ Group ──────────

/**
 * Placing, unplacing and moving all move a question between "held by a group"
 * and "held by nothing", which is what the catalogue list cuts on — so all
 * three invalidate it alongside the tree.
 */
export function usePlaceQuestion() {
  return useMutationWithInvalidation(
    async ({
      groupId,
      questionId,
    }: {
      groupId: number
      questionId: number
    }) => {
      const { data } = await apiClient.post(
        `/admin/configurator/groups/${groupId}/questions/${questionId}`,
      )
      return data
    },
    QUESTION_KEYS,
  )
}

export function useUnplaceQuestion() {
  return useMutationWithInvalidation(
    async ({
      groupId,
      questionId,
      answer,
    }: {
      groupId: number
      questionId: number
      answer?: WriteAnswer
    }) => {
      await apiClient.delete(
        `/admin/configurator/groups/${groupId}/questions/${questionId}`,
        answering(answer),
      )
    },
    QUESTION_KEYS,
  )
}

/**
 * A question dragged out of one area and into another.
 *
 * One request rather than an unassign and an assign: `order` is the target's
 * block as the drop leaves it, so the two halves have to land together or the
 * question arrives at the end of a block it was dropped into the middle of.
 */
export function useMoveQuestion() {
  return useMutationWithInvalidation(
    async ({
      fromGroupId,
      questionId,
      toGroupId,
      order,
      answer,
    }: {
      fromGroupId: number
      questionId: number
      toGroupId: number
      order: number[]
      answer?: WriteAnswer
    }) => {
      await apiClient.patch(
        `/admin/configurator/groups/${fromGroupId}/questions/${questionId}/move`,
        { toGroupId, order },
        answering(answer),
      )
    },
    QUESTION_KEYS,
  )
}

// ────────── CMS: Placements ──────────

/**
 * A measure gained or lost a place. On top of the question's own two lists
 * that moves the measure list, where a placement is what the "in
 * configurator" column counts — which is why the writes that only reorder
 * placements or change which is the default take `QUESTION_KEYS` instead: the
 * measure list holds the same rows either way, and it is the whole catalogue.
 */
const PLACEMENT_KEYS = [...QUESTION_KEYS, [...CATALOGUE_TREE_KEY, 'MEASURE']]

/**
 * Places several measures under one question.
 *
 * One request each, because that is what the endpoint takes — but one
 * invalidation at the end rather than one per measure, so a dialog that placed
 * six does not refetch the tree six times.
 */
export function useAssignPlacements() {
  return useMutationWithInvalidation(
    async (dto: { questionId: number; measureIds: number[] }) => {
      for (const measureId of dto.measureIds) {
        await apiClient.post('/admin/configurator/placements', {
          questionId: dto.questionId,
          measureId,
        })
      }
    },
    PLACEMENT_KEYS,
    // A loop can commit part of its work and then throw; without this the
    // placements that landed stay invisible until the next refetch.
    { invalidateOn: 'settled' },
  )
}

export function usePatchPlacement() {
  return useMutationWithInvalidation(
    async ({ id, dto }: { id: number; dto: { isDefault?: boolean } }) => {
      const { data } = await apiClient.patch(
        `/admin/configurator/placements/${id}`,
        dto,
      )
      return data
    },
    QUESTION_KEYS,
  )
}

/** Takes a measure out of a question — see `usePatchGroup` on the
 *  confirmation; this is the change that reaches the most positions. */
export function useUnassignPlacement() {
  return useMutationWithInvalidation(
    ({ id, answer }: { id: number; answer?: WriteAnswer }) =>
      apiClient.delete(
        `/admin/configurator/placements/${id}`,
        answering(answer),
      ),
    PLACEMENT_KEYS,
  )
}

export const useSortPlacements = makeSortHook(
  '/admin/configurator/placements/sort',
  QUESTION_KEYS,
  // The tree is the cache that carries a placement's position; the CMS
  // question list projects the options without it.
  { listKey: 'options', keys: [CONFIGURATOR_TREE_KEY] },
)

// ────────── CMS: Question displayed property keys ──────────

export function useQuestionPropertyKeys(questionId: number | null) {
  return useQuery({
    queryKey: [...CONFIGURATOR_CMS_QUESTIONS_KEY, questionId, 'property-keys'],
    enabled: questionId != null,
    queryFn: async (): Promise<ConfiguratorDisplayedProperty[]> => {
      const { data } = await apiClient.get<ConfiguratorDisplayedProperty[]>(
        `/admin/configurator/questions/${questionId}/property-keys`,
      )
      return data
    },
  })
}

/**
 * The axes one question's cards compare, in the order they are drawn.
 *
 * Written as pairs although the endpoint stores keys: the picker has the label
 * of whatever it just added, and the cache it writes into is read by surfaces
 * that print the label — handing them keys would flash a camelCase heading
 * until the refetch lands.
 */
export function useSetDisplayedPropertyKeys() {
  return useMutationWithInvalidation(
    async ({
      questionId,
      properties,
    }: {
      questionId: number
      properties: ConfiguratorDisplayedProperty[]
    }) => {
      const { data } = await apiClient.patch(
        `/admin/configurator/questions/${questionId}/displayed-property-keys`,
        { keys: properties.map(property => property.key) },
      )
      return data
    },
    QUESTION_KEYS,
    {
      // The list is the question's own, in the order it is drawn, so the write
      // is the order: found by the question it hangs on rather than by the
      // entries inside it.
      optimistic: {
        keys: QUESTION_KEYS,
        apply: (cached, { questionId, properties }) =>
          withQuestion(cached, questionId, question => ({
            ...question,
            displayedProperties: properties,
          })),
      },
    },
  )
}

/** The cached tree with one question rewritten, wherever it hangs. */
function withQuestion(
  cached: unknown,
  questionId: number,
  write: (question: Record<string, unknown>) => Record<string, unknown>,
): unknown {
  return mapDeep(cached, node => {
    const question = node as Record<string, unknown> | null
    return question &&
      typeof question === 'object' &&
      question.id === questionId &&
      'displayedProperties' in question
      ? write(question)
      : undefined
  })
}

// ────────── CMS: Bulk Sort (non-standard) ──────────

export function useSortGroupQuestions() {
  return useMutationWithInvalidation(
    async ({
      groupId,
      items,
    }: {
      groupId: number
      items: Array<{ id: number; sortOrder: number }>
    }) => {
      await apiClient.patch(
        `/admin/configurator/groups/${groupId}/questions/sort`,
        { items },
      )
    },
    [CONFIGURATOR_TREE_KEY],
    {
      // The questions sit as deep in the tree as their group.
      optimistic: {
        keys: [CONFIGURATOR_TREE_KEY],
        apply: (cached, { items }) =>
          applySortOrder(cached, items, { listKey: 'questions' }),
      },
    },
  )
}
